import React from 'react';
import styled from 'styled-components';
import { FeedbackCommentProps, FeedbackSectionId, UserType } from './utils';
import {
  Answer,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
} from './api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import RenderAnswers from './RenderAnswers';
import FeedbackQuestionTitleRender from './FeedbackQuestionTitleRender';
import { INDICATOR_SUMMARY_QUESTION_ID } from 'npo/pages/NPOFeedback/utils';
import RenderImpactModel from './RenderImpactModel';
import FeedbackCommentsSection from './FeedbackCommentsSection';
import FeedbackAnswer from 'npo/pages/BetaFeedback/Feedback/FeedbackAnswer';
import { FeedbackApi } from '../api';
import {
  budgetQuestionIds,
  getBudgetDollarAmount,
} from 'npo/pages/ReportBuilder/Components/Budget/utils';

interface FeedbackCommentRowRenderProps {
  open: boolean;
  questionId: number;
  reportId: number;
  title: string;
  userName: string;
  userType: UserType;
  report: FeedbackApi;
  feedbackSectionId: FeedbackSectionId;
  currentUserId?: string;
  currentUserEmail?: string;
  className?: string;
  answer: Answer;
  allAnswers: Answer[];
  comments: FeedbackCommentProps[];
  handleOpen: () => void;
  deleteComment: (questionId: number, index: number) => unknown;
  addOrUpdateComment: (
    questionId: number,
    comment: FeedbackCommentProps,
    index?: number,
  ) => unknown;
}

export const StyledCommentRowRender = styled.div<{ open: boolean }>`
  background-color: ${({ open }) =>
    open ? COLORS.grayLightest : COLORS.whiteBase};
  .pointer {
    cursor: pointer;
  }
  .row-wrapper {
    padding: ${SPACERS.medium} ${SPACERS.extraLarge};
    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid ${COLORS.grayLightest};
      .title {
        flex: 1;
        padding-right: ${SPACERS.tiny};
      }
    }
  }
  .textarea {
  }
`;

const getBudgetAnswer = (
  questionId: number,
  answer: Answer,
  allAnswers: Answer[],
) => {
  if (budgetQuestionIds.includes(questionId)) {
    const budget = getBudgetDollarAmount(answer.text);
    return budget
      ? answer
      : allAnswers.find(a => a.question.dbId === 80011) || answer;
  }
  return answer;
};

const FeedbackCommentRowRender: React.FC<FeedbackCommentRowRenderProps> = ({
  open,
  answer,
  allAnswers,
  userName,
  reportId,
  report,
  questionId,
  currentUserId,
  currentUserEmail,
  userType,
  title,
  feedbackSectionId,
  comments,
  className,
  handleOpen,
  addOrUpdateComment,
  deleteComment,
}) => {
  if (questionId === INDICATOR_SUMMARY_QUESTION_ID) {
    return (
      <RenderImpactModel
        answer={answer}
        report={report}
        reportId={reportId}
        comments={comments}
        questionId={questionId}
        allAnswers={allAnswers}
        sectionId={feedbackSectionId}
        deleteQuestionLevelComment={deleteQuestionLevelComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        onSuccessDelete={deleteComment}
        onSuccessSave={addOrUpdateComment}
        addOrUpdateComment={addOrUpdateComment}
      />
    );
  }
  const disabledClick = false;
  const a: Answer = getBudgetAnswer(questionId, answer, allAnswers);
  return (
    <StyledCommentRowRender open={open} className={className}>
      <div
        onClick={disabledClick ? undefined : handleOpen}
        className={disabledClick ? 'row-wrapper' : 'row-wrapper pointer'}
      >
        <FeedbackQuestionTitleRender
          title={title}
          disabled={disabledClick}
          commentsLength={comments.length}
        />
        {feedbackSectionId === 'custom_questions' ? (
          <RenderAnswers
            answer={answer}
            reportId={reportId}
            questionId={questionId}
            sectionId={feedbackSectionId}
          />
        ) : (
          <FeedbackAnswer questionId={questionId} answer={a} />
        )}
      </div>
      {open && (
        <FeedbackCommentsSection
          comments={comments}
          userType={userType}
          currentUserEmail={currentUserEmail}
          userName={userName}
          reportId={reportId}
          questionId={questionId}
          feedbackSectionId={feedbackSectionId}
          answer={answer}
          deleteQuestionLevelComment={deleteQuestionLevelComment}
          updateQuestionLevelComment={updateQuestionLevelComment}
          onSuccessDelete={deleteComment}
          onSuccessSave={addOrUpdateComment}
          addOrUpdateComment={addOrUpdateComment}
        />
      )}
    </StyledCommentRowRender>
  );
};

export default FeedbackCommentRowRender;
